/**
 * SignUp Control State Machine
 *
 * @note step_number is explicitly defined rather than derived from array position.
 * This supports A/B testing where steps may be reordered (e.g., skip logo step but keep
 * numbering as 1,2,4,5...). Also handles implicit steps that
 * aren't UI-bound which would break auto-counting. Requires manual maintenance.
 */
import { createMachine, assign } from "xstate";
import { SignUpEvent } from "../constants/signup.events";
import { SignUpFormField } from "../constants/signup-form-field.enum";
import { SignUpStep } from "../constants/signup-step.enum";
import { RevenueValue, YearsOpenValue } from "../constants/signup-value.enum";

export interface CompanyOption {
  label: string;
  value?: string;
  [key: string]: any;
}

export interface FormData {
  [SignUpFormField.Company]: CompanyOption | null;
  [SignUpFormField.Country]: string | null;
  [SignUpFormField.YearsOpen]: string | null;
  [SignUpFormField.Revenue]: string | null;
  [SignUpFormField.Vertical]: string | null;
  [SignUpFormField.BusinessNeeds]: string[];
  [SignUpFormField.CurrentSoftware]: string | null;
  [SignUpFormField.FirstName]: string;
  [SignUpFormField.LastName]: string;
  [SignUpFormField.Email]: string;
  [SignUpFormField.Phone]: string;
  [SignUpFormField.Url]: string;
  [SignUpFormField.Code]: string;
  [SignUpFormField.Password]: string;
}

interface MachineContext {
  formData: FormData;
  subdomain?: string;
  clientUuid?: string;
  utm?: Record<string, any>;
}

type MachineEvents =
  | { type: "NEXT" }
  | { type: "PREV" }
  | { type: "QUALIFIED" }
  | { type: "DISQUALIFIED" }
  | { type: "DEMO_BOOKED" }
  | { type: "DEMO_SKIPPED" }
  | { type: "UPDATE_FORM"; data: Partial<FormData> }
  | { type: "SET_SUBDOMAIN"; subdomain: string }
  | { type: "SET_CLIENT_UUID"; uuid: string }
  | { type: "SET_UTM"; utm: Record<string, any> };

export const signupMachine = createMachine<MachineContext, MachineEvents>({
  predictableActionArguments: true,
  id: "signup-2025-02-20",
  initial: SignUpStep.Company,
  context: {
    formData: {
      [SignUpFormField.Company]: null,
      [SignUpFormField.Country]: null,
      [SignUpFormField.YearsOpen]: null,
      [SignUpFormField.Revenue]: null,
      [SignUpFormField.Vertical]: null,
      [SignUpFormField.BusinessNeeds]: [],
      [SignUpFormField.CurrentSoftware]: null,
      [SignUpFormField.FirstName]: "",
      [SignUpFormField.LastName]: "",
      [SignUpFormField.Email]: "",
      [SignUpFormField.Phone]: "",
      [SignUpFormField.Url]: "",
      [SignUpFormField.Code]: "",
      [SignUpFormField.Password]: "",
    },
    subdomain: undefined,
    clientUuid: undefined,
    utm: undefined,
  },
  states: {
    [SignUpStep.Company]: {
      meta: {
        id: "step-company",
        step_number: 1,
        analytics: {
          event: SignUpEvent.CompanyNameSubmitted,
        },
      },
      on: {
        NEXT: SignUpStep.Country,
      },
    },
    [SignUpStep.Country]: {
      meta: {
        id: "step-country",
        step_number: 2,
        analytics: {
          event: SignUpEvent.CountrySubmitted,
        },
      },
      on: {
        PREV: SignUpStep.Company,
        NEXT: SignUpStep.YearsOpen,
      },
    },
    [SignUpStep.YearsOpen]: {
      meta: {
        id: "step-years-open",
        step_number: 3,
        analytics: {
          event: SignUpEvent.BusinessAgeSubmitted,
        },
      },
      on: {
        PREV: SignUpStep.Country,
        NEXT: [
          {
            target: SignUpStep.Vertical,
            cond: (context) =>
              context.formData[SignUpFormField.YearsOpen]?.toLowerCase() ===
              YearsOpenValue.NotOpenYet.toLowerCase(),
            actions: assign({
              formData: (ctx) => ({
                ...ctx.formData,
                [SignUpFormField.Revenue]: RevenueValue.LessThan8K,
              }),
            }),
          },
          { target: SignUpStep.Revenue },
        ],
      },
    },
    [SignUpStep.Revenue]: {
      meta: {
        id: "step-revenue",
        step_number: 4,
        analytics: {
          event: SignUpEvent.RevenueSubmitted,
        },
      },
      on: {
        PREV: SignUpStep.YearsOpen,
        NEXT: SignUpStep.Vertical,
      },
    },
    [SignUpStep.Vertical]: {
      meta: {
        id: "step-vertical",
        step_number: 5,
        analytics: {
          event: SignUpEvent.BusinessSubtypeSubmitted,
        },
      },
      on: {
        PREV: SignUpStep.Revenue,
        NEXT: SignUpStep.BusinessNeeds,
      },
    },
    [SignUpStep.BusinessNeeds]: {
      meta: {
        id: "step-business-needs",
        step_number: 6,
        analytics: {
          event: SignUpEvent.BusinessProblemsSubmitted,
        },
      },
      on: {
        PREV: SignUpStep.Vertical,
        NEXT: SignUpStep.CurrentSoftware,
      },
    },
    [SignUpStep.CurrentSoftware]: {
      meta: {
        id: "step-current-software",
        step_number: 7,
        analytics: {
          event: SignUpEvent.CurrentSoftwareSubmitted,
        },
      },
      on: {
        PREV: SignUpStep.BusinessNeeds,
        NEXT: SignUpStep.ContactInfo,
      },
    },
    [SignUpStep.ContactInfo]: {
      meta: {
        id: "step-contact-info",
        step_number: 8,
        analytics: {
          event: SignUpEvent.ContactInfoSubmitted,
        },
      },
      on: {
        PREV: SignUpStep.CurrentSoftware,
        DISQUALIFIED: SignUpStep.Free,
      },
    },
    [SignUpStep.Free]: {
      meta: {
        id: "step-free",
        step_number: 9,
        analytics: {
          event: SignUpEvent.FreeSignupSubmitted,
        },
      },
      on: {
        NEXT: SignUpStep.AccountConfirmation,
      },
    },
    [SignUpStep.AccountConfirmation]: {
      meta: {
        id: "step-account-confirmation",
        step_number: 10,
        analytics: {
          event: SignUpEvent.AccountConfirmationSubmitted,
        },
      },
      on: {
        NEXT: SignUpStep.CreatePassword,
      },
    },
    [SignUpStep.CreatePassword]: {
      meta: {
        id: "step-create-password",
        step_number: 11,
        analytics: {
          event: SignUpEvent.CreatePasswordSubmitted,
        },
      },
    },
    [SignUpStep.Final]: {
      type: "final",
    },
  },
  on: {
    SET_SUBDOMAIN: {
      actions: assign({ subdomain: (_, event) => event.subdomain }),
    },
    SET_CLIENT_UUID: {
      actions: assign({ clientUuid: (_, event) => event.uuid }),
    },
    UPDATE_FORM: {
      actions: assign({
        formData: (context, event) => {
          const updatedFormData = {
            ...context.formData,
            ...event.data,
          };
          return updatedFormData;
        },
      }),
    },
    SET_UTM: {
      actions: assign({ utm: (_, event) => event.utm }),
    },
  },
});
